import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBuilding } from '../../contexts/BuildingContext';
import { LuUpload, LuX, LuFileText, LuImage, LuLock, LuShieldCheck } from 'react-icons/lu';
import { toast } from 'sonner';

export default function CreateFaultForm({ onSubmit, onCancel }) {
  const { t } = useTranslation();
  const { currentBuilding, currentProperty, availableBuildings } = useBuilding();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    scope: 'BUILDING',
    building_id: currentBuilding?.id || '',
    title: '',
    description: '',
    category: 'Other',
    location: '',
    priority: 'Medium',
    is_anonymous: false
  });

  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const filteredBuildings = useMemo(() => {
    if (!currentProperty) return [];
    return availableBuildings.filter((b) => b.property_id === currentProperty.id);
  }, [availableBuildings, currentProperty]);

  const categories = [
  { value: 'Electrical', label: t('faultReports.categoryElectrical') },
  { value: 'Plumbing', label: t('faultReports.categoryPlumbing') },
  { value: 'Elevator', label: t('faultReports.categoryElevator') },
  { value: 'Painting', label: t('faultReports.categoryPainting') },
  { value: 'Landscaping', label: t('faultReports.categoryLandscaping') },
  { value: 'Security', label: t('faultReports.categorySecurity') },
  { value: 'Other', label: t('faultReports.categoryOther') }];


  const priorities = [
  { value: 'Low', label: t('faultReports.priorityLow') },
  { value: 'Medium', label: t('faultReports.priorityMedium') },
  { value: 'High', label: t('faultReports.priorityHigh') },
  { value: 'Urgent', label: t('faultReports.priorityUrgent') }];


  const isImage = (fileName) => {
    return /\.(jpg|jpeg|png|webp|avif|gif|svg)$/i.test(fileName);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (attachments.length + files.length > 5) {
      toast.error(t('faultReports.maxFilesCount', { count: 5 }));
      return;
    }

    setUploading(true);
    try {
      const uploadedFiles = await Promise.all(
        files.map(async (file) => {
          const { data: fileId, error } = await window.ezsite.apis.upload({
            filename: file.name,
            file: file
          });
          if (error) throw error;

          const { data: res, error: urlError } = await window.ezsite.apis.getUploadUrl(fileId);
          if (urlError) throw urlError;

          const urlString = typeof res === 'string' ? res : res?.url;
          if (!urlString) throw new Error('Failed to get valid upload URL');

          return { fileId, url: urlString, name: file.name };
        })
      );

      setAttachments((prev) => [...prev, ...uploadedFiles]);
      toast.success(t('common.success'));
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(t('common.error'));
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error(t('faultReports.titleRequired'));
      return;
    }
    if (formData.scope === 'BUILDING' && !formData.building_id) {
      toast.error(t('faultReports.selectBuilding'));
      return;
    }
    setLoading(true);
    try {
      await onSubmit(formData, attachments);
    } catch (error) {
      console.error('Submit failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Scope Selection */}
      <div className="space-y-4">
        <label className="text-sm font-bold text-gray-700 uppercase tracking-widest ml-1">{t('faultReports.scope')}</label>
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setFormData({ ...formData, scope: 'PROPERTY', building_id: null })}
            className={`p-4 border-2 rounded-2xl text-left transition-all ${formData.scope === 'PROPERTY' ? 'border-blue-600 bg-blue-50/50 ring-4 ring-blue-50' : 'border-gray-100 bg-gray-50 text-gray-400 hover:bg-gray-100'}`}>

            <div className={`text-sm font-bold mb-1 ${formData.scope === 'PROPERTY' ? 'text-blue-700' : 'text-gray-500'}`}>{t('faultReports.propertyWide')}</div>
            <div className="text-[10px] font-medium text-gray-400 leading-tight">{t('faultReports.propertyWideDesc')}</div>
          </button>
          <button
            type="button"
            onClick={() => setFormData({ ...formData, scope: 'BUILDING' })}
            className={`p-4 border-2 rounded-2xl text-left transition-all ${formData.scope === 'BUILDING' ? 'border-blue-600 bg-blue-50/50 ring-4 ring-blue-50' : 'border-gray-100 bg-gray-50 text-gray-400 hover:bg-gray-100'}`}>

            <div className={`text-sm font-bold mb-1 ${formData.scope === 'BUILDING' ? 'text-blue-700' : 'text-gray-500'}`}>{t('faultReports.buildingSpecific')}</div>
            <div className="text-[10px] font-medium text-gray-400 leading-tight">{t('faultReports.buildingSpecificDesc')}</div>
          </button>
        </div>
      </div>

      {formData.scope === 'BUILDING' &&
      <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
          <label className="text-sm font-bold text-gray-700 uppercase tracking-widest ml-1">{t('faultReports.selectBuilding')} <span className="text-red-500">*</span></label>
          <select
          value={formData.building_id || ''}
          onChange={(e) => setFormData({ ...formData, building_id: e.target.value })}
          className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all font-medium">

            <option value="">{t('faultReports.selectBuilding')}...</option>
            {filteredBuildings.map((b) =>
          <option key={b.id} value={b.id}>{b.name}</option>
          )}
          </select>
        </div>
      }

      {/* Title & Location */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-700 uppercase tracking-widest ml-1">{t('faultReports.issueTitle')} <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all font-medium placeholder:text-gray-300"
            placeholder={t('faultReports.whatIsIssue')} />

        </div>
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-700 uppercase tracking-widest ml-1">{t('faultReports.specificLocation')}</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all font-medium placeholder:text-gray-300"
            placeholder={t('faultReports.locationPlaceholder')} />

        </div>
      </div>

      {/* Category Selection */}
      <div className="space-y-4">
        <label className="text-sm font-bold text-gray-700 uppercase tracking-widest ml-1">{t('faultReports.category')}</label>
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) =>
          <button
            key={cat.value}
            type="button"
            onClick={() => setFormData({ ...formData, category: cat.value })}
            className={`px-4 py-2.5 rounded-full text-xs font-bold border-2 transition-all ${
            formData.category === cat.value ?
            'bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-100' :
            'bg-white border-gray-100 text-gray-500 hover:border-gray-300'}`
            }>

              {cat.label}
            </button>
          )}
        </div>
      </div>

      {/* Priority Selection */}
      <div className="space-y-4">
        <label className="text-sm font-bold text-gray-700 uppercase tracking-widest ml-1">{t('faultReports.priority')}</label>
        <div className="grid grid-cols-4 gap-3">
          {priorities.map((p) =>
          <button
            key={p.value}
            type="button"
            onClick={() => setFormData({ ...formData, priority: p.value })}
            className={`py-3 rounded-xl text-[10px] font-black uppercase tracking-widest border-2 transition-all ${
            formData.priority === p.value ?
            'bg-blue-600 border-blue-600 text-white' :
            'bg-gray-50 border-gray-50 text-gray-400 hover:bg-gray-100'}`
            }>

              {p.label}
            </button>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <label className="text-sm font-bold text-gray-700 uppercase tracking-widest ml-1">{t('faultReports.description')}</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={4}
          className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all font-medium resize-none placeholder:text-gray-300"
          placeholder={t('faultReports.provideDetails')} />

      </div>

      {/* Attachments */}
      <div className="space-y-4">
        <label className="text-sm font-bold text-gray-700 uppercase tracking-widest ml-1">{t('faultReports.attachments')} ({t('faultReports.maxFiles')})</label>
        <div className="border-2 border-dashed border-gray-200 rounded-3xl p-10 text-center hover:border-blue-400 hover:bg-blue-50/30 transition-all cursor-pointer relative group">
          <input
            type="file"
            multiple
            onChange={handleFileSelect}
            className="absolute inset-0 opacity-0 cursor-pointer"
            disabled={uploading || attachments.length >= 5} />

          <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-100 transition-colors">
            <LuUpload className="w-8 h-8 text-gray-300 group-hover:text-blue-500 transition-colors" />
          </div>
          <p className="text-sm font-bold text-gray-700">
            {uploading ? t('faultReports.uploading') : attachments.length >= 5 ? t('faultReports.maxFiles') : t('faultReports.uploadFiles')}
          </p>
          <p className="text-xs text-gray-400 mt-1">{t('faultReports.fileTypes')}</p>
        </div>
        
        {attachments.length > 0 &&
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            {attachments.map((file, index) =>
          <div key={index} className="flex items-center justify-between p-4 bg-white rounded-2xl border border-gray-100 shadow-sm animate-in zoom-in-95 duration-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-50 rounded-lg">
                    {isImage(file.name) ? <LuImage className="text-blue-500" /> : <LuFileText className="text-orange-500" />}
                  </div>
                  <span className="text-sm font-bold text-gray-700 truncate max-w-[150px]">{file.name}</span>
                </div>
                <button
              type="button"
              onClick={() => removeAttachment(index)}
              className="p-2 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-xl transition-colors">

                  <LuX className="w-4 h-4" />
                </button>
              </div>
          )}
          </div>
        }
      </div>

      {/* Privacy Toggle */}
      <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${formData.is_anonymous ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}`}>
            {formData.is_anonymous ? <LuLock className="w-5 h-5" /> : <LuShieldCheck className="w-5 h-5" />}
          </div>
          <div>
            <p className="text-sm font-bold text-gray-700">{t('faultReports.anonymous')}</p>
            <p className="text-[10px] font-medium text-gray-500">{t('faultReports.anonymousDesc')}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setFormData({ ...formData, is_anonymous: !formData.is_anonymous })}
          className={`w-12 h-6 rounded-full transition-all relative ${formData.is_anonymous ? 'bg-orange-500' : 'bg-gray-200'}`}>

          <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.is_anonymous ? 'left-7' : 'left-1'}`} />
        </button>
      </div>

      {/* Footer Actions */}
      <div className="flex items-center gap-4 pt-6 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-6 py-4 border-2 border-gray-100 rounded-2xl font-bold text-gray-500 hover:bg-gray-50 hover:border-gray-200 transition-all">

          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={loading || uploading}
          className="flex-2 px-10 py-4 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 shadow-xl shadow-blue-100 transition-all disabled:opacity-50 active:scale-95">

          {loading ?
          <div className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>{t('faultReports.submitting')}</span>
            </div> :

          t('faultReports.submitReport')
          }
        </button>
      </div>
    </form>);

}